/**
 * Pixel buffers and the shared shape every filter works on.
 *
 * `ImageData` from the DOM satisfies this structurally, so `io/` can hand a live
 * ImageData straight in while `core/` stays free of browser types.
 */

export interface PixelBuffer {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export const clampByte = (value: number): number =>
  value < 0 ? 0 : value > 255 ? 255 : (value + 0.5) | 0;

/** Rec. 709 luma — the same weights everywhere we need "how bright is this". */
export const luma = (red: number, green: number, blue: number): number =>
  0.2126 * red + 0.7152 * green + 0.0722 * blue;

/**
 * Premultiply a buffer in place.
 *
 * Blurring straight-alpha pixels drags the colour of transparent regions into
 * visible ones — a red circle on a transparent layer smears black edges. Blur
 * premultiplied, then undo it, and the edges stay red.
 */
export function premultiply(buffer: PixelBuffer): void {
  const { data } = buffer;
  for (let i = 0; i < data.length; i += 4) {
    const a = (data[i + 3] ?? 0) / 255;
    data[i] = (data[i] ?? 0) * a;
    data[i + 1] = (data[i + 1] ?? 0) * a;
    data[i + 2] = (data[i + 2] ?? 0) * a;
  }
}

export function unpremultiply(buffer: PixelBuffer): void {
  const { data } = buffer;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3] ?? 0;
    if (a === 0 || a === 255) continue;
    const scale = 255 / a;
    data[i] = (data[i] ?? 0) * scale;
    data[i + 1] = (data[i + 1] ?? 0) * scale;
    data[i + 2] = (data[i + 2] ?? 0) * scale;
  }
}

/** Copy a buffer so a preview pass cannot corrupt the source pixels. */
export function cloneBuffer(buffer: PixelBuffer): PixelBuffer {
  return {
    data: new Uint8ClampedArray(buffer.data),
    width: buffer.width,
    height: buffer.height,
  };
}
